import React from 'react'
import PropTypes from 'prop-types'
import { useHistory } from 'react-router-dom'
import { subIsPlus, subIsActive, subIsTeams, subIsIap, subIsIos, isHeatStressSubscription } from './'
import { isAndroidApp, isIosApp } from '../../common/ambient'

/**
 * Helper function to determine if upgrade/downgrade action is available
 * @param {Object} sub - Subscription object
 * @returns {string|false} - 'upgrade', 'downgrade', or false if no action available
 */
export function getUpgradeDowngradeAction (sub) {
  // Heat Stress subscription logic
  if (isHeatStressSubscription(sub.productName)) {
    if (!subIsActive(sub)) {
      return false
    }
    if (sub.willDowngradeAtPeriodEnd) {
      return false
    }

    const isStandard = /standard/.test(sub.productName)
    const isEnterprise = /enterprise/.test(sub.productName)

    if (isStandard) return 'upgrade'
    if (isEnterprise) return 'downgrade'
    return 'upgrade' // Pro can upgrade to Enterprise
  }

  // AWN+ subscription logic
  if (!subIsPlus(sub)) {
    return false
  }
  if (!subIsActive(sub)) {
    return false
  }
  if (sub.willDowngradeAtPeriodEnd) {
    return false
  }

  // Don't show upgrade link for IAP subscriptions when not on the matching platform
  if (subIsIap(sub)) {
    if (subIsIos(sub) && !isIosApp()) {
      return false  // Apple subscription but not on iOS device
    }
    if (!subIsIos(sub) && !isAndroidApp()) {
      return false  // Google subscription but not on Android device
    }
  }

  const isTeams = subIsTeams(sub)
  return isTeams ? 'downgrade' : 'upgrade'
}

// Upgrade/Downgrade link component for AWN Plus subscriptions
function UpgradeDowngradeLink ({ sub, user }) {
  const history = useHistory()
  const action = getUpgradeDowngradeAction(sub)

  if (!action) {
    return null
  }

  const handleClick = () => {
    // Navigate to subscribe page based on subscription type
    if (isHeatStressSubscription(sub.productName)) {
      history.push('/subscriptions/subscribe?type=heat-stress')
    } else {
      // AWN+ subscription - navigate to awn-plus subscribe page
      // The subscribe page will detect the user's current subscription state
      // and show the appropriate upgrade or downgrade preview
      history.push('/subscriptions/subscribe?type=awn-plus')
    }
  }

  return (
    <a
      className={`${action}-link`}
      onClick={handleClick}
    >
      {action === 'downgrade' ? 'Downgrade' : 'Upgrade'}
    </a>
  )
}

UpgradeDowngradeLink.propTypes = {
  sub: PropTypes.object.isRequired,
  user: PropTypes.object.isRequired
}

UpgradeDowngradeLink.displayName = 'UpgradeDowngradeLink'

export default UpgradeDowngradeLink
